import { useEffect, useRef, useState } from 'react';

import type SFilter from '@/features/StructuredFilter/class/SFilter';
import type { DateRangePickerValue } from '@/shared/ui/date-range-picker';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { getTimezoneDayjs } from '@/utils/time';

type FilterUiState = {
  signature: string;
  dateRangeValue: DateRangePickerValue;
  dataFilter: StructuredFilterQueryFormatT[];
};

export type SubmittedFilterState = FilterUiState & {
  chatAlias: Alias;
};

type Params = {
  selectedChatAlias: Alias | null;
  loadedChatAlias: Alias | null;
  initialDateRange: DateRangePickerValue;
  historyFilterSnapshot: {
    filter: SFilter | null;
    formats: StructuredFilterQueryFormatT[] | null;
  } | null;
  initialFilter: SFilter | null;
  submittedFilterState: SubmittedFilterState | null;
  parseStructuredFilter: (
    structuredFilter: StructuredFilter,
  ) => StructuredFilterQueryFormatT[];
};

const normalizeDateRangeValue = (dateRangeValue: DateRangePickerValue) => {
  if (dateRangeValue.type === 'preset') {
    return {
      type: 'preset',
      presetName: dateRangeValue.presetName,
    };
  }

  return {
    type: 'dateRange',
    from: dateRangeValue.dateRange.from?.toJSON() ?? null,
    to: dateRangeValue.dateRange.to?.toJSON() ?? null,
  };
};

const buildFilterUiState = ({
  dateRangeValue,
  dataFilter,
}: Omit<FilterUiState, 'signature'>): FilterUiState => {
  return {
    signature: JSON.stringify({
      dateRangeValue: normalizeDateRangeValue(dateRangeValue),
      dataFilter,
    }),
    dateRangeValue,
    dataFilter,
  };
};

const buildEmptyFilterState = (
  initialDateRange: DateRangePickerValue,
): FilterUiState =>
  buildFilterUiState({
    dateRangeValue: initialDateRange,
    dataFilter: [],
  });

const buildRestoredFilterState = ({
  seedFilter,
  seedFormats,
  initialDateRange,
  parseStructuredFilter,
}: {
  seedFilter: SFilter;
  seedFormats: StructuredFilterQueryFormatT[] | null;
  initialDateRange: DateRangePickerValue;
  parseStructuredFilter: (
    structuredFilter: StructuredFilter,
  ) => StructuredFilterQueryFormatT[];
}): FilterUiState => {
  const dateRange = seedFilter.getDateFilter();
  const restFilter = seedFilter.getDataFilterMap();
  const nextDataFilter = seedFormats
    ? seedFormats.filter(
        (format) =>
          format.columnKey !== FEEDBACK_COLUMNS.DATE_START &&
          format.columnKey !== FEEDBACK_COLUMNS.DATE_END,
      )
    : parseStructuredFilter(restFilter);

  const dateRangeValue =
    dateRange.start_date && dateRange.end_date
      ? {
          type: 'dateRange' as const,
          dateRange: {
            from: getTimezoneDayjs(dateRange.start_date),
            to: getTimezoneDayjs(dateRange.end_date),
          },
        }
      : initialDateRange;

  return buildFilterUiState({
    dateRangeValue,
    dataFilter: nextDataFilter,
  });
};

export const buildSubmittedFilterState = ({
  chatAlias,
  dateRangeValue,
  dataFilter,
}: {
  chatAlias: Alias;
  dateRangeValue: DateRangePickerValue;
  dataFilter: StructuredFilterQueryFormatT[];
}): SubmittedFilterState => {
  const nextState = buildFilterUiState({
    dateRangeValue,
    dataFilter,
  });

  return {
    ...nextState,
    chatAlias,
  };
};

export const useChatInputFilterState = ({
  selectedChatAlias,
  loadedChatAlias,
  initialDateRange,
  historyFilterSnapshot,
  initialFilter,
  submittedFilterState,
  parseStructuredFilter,
}: Params) => {
  const appliedStateSignatureRef = useRef<string | null>(null);
  const [dataFilter, setDataFilter] = useState<StructuredFilterQueryFormatT[]>(
    [],
  );
  const [dateRangeValue, setDateRangeValue] =
    useState<DateRangePickerValue>(initialDateRange);

  const applyState = (nextState: FilterUiState) => {
    if (appliedStateSignatureRef.current === nextState.signature) {
      return;
    }

    appliedStateSignatureRef.current = nextState.signature;
    setDateRangeValue(nextState.dateRangeValue);
    setDataFilter(nextState.dataFilter);
  };

  useEffect(() => {
    const emptyFilterState = buildEmptyFilterState(initialDateRange);

    if (!selectedChatAlias) {
      if (!initialFilter) {
        applyState(emptyFilterState);
        return;
      }

      applyState(
        buildRestoredFilterState({
          seedFilter: initialFilter,
          seedFormats: null,
          initialDateRange,
          parseStructuredFilter,
        }),
      );
      return;
    }

    if (selectedChatAlias !== loadedChatAlias) {
      if (submittedFilterState?.chatAlias === selectedChatAlias) {
        applyState(submittedFilterState);
      }
      return;
    }

    if (submittedFilterState?.chatAlias === selectedChatAlias) {
      if (historyFilterSnapshot?.filter) {
        const restoredFilterState = buildRestoredFilterState({
          seedFilter: historyFilterSnapshot.filter,
          seedFormats: historyFilterSnapshot.formats,
          initialDateRange,
          parseStructuredFilter,
        });

        if (restoredFilterState.signature === submittedFilterState.signature) {
          applyState(restoredFilterState);
          return;
        }
      }

      applyState(submittedFilterState);
      return;
    }

    if (historyFilterSnapshot?.filter) {
      applyState(
        buildRestoredFilterState({
          seedFilter: historyFilterSnapshot.filter,
          seedFormats: historyFilterSnapshot.formats,
          initialDateRange,
          parseStructuredFilter,
        }),
      );
      return;
    }

    applyState(emptyFilterState);
  }, [
    selectedChatAlias,
    loadedChatAlias,
    initialDateRange,
    historyFilterSnapshot,
    initialFilter,
    submittedFilterState,
    parseStructuredFilter,
  ]);

  return {
    dataFilter,
    setDataFilter,
    dateRangeValue,
    setDateRangeValue,
  };
};
